// frontend/src/pages/supplierSchedule.js
//
// How a supplier's ordering cadence is put into words.
//
// Both helpers exist because the stored pair (review_frequency,
// preferred_order_day) does not read as what it means. The day column holds a
// bare weekday 0..6, and under a MONTHLY cadence that Monday means "the first
// Monday of each month" — so a picker, a grid cell or a summary that renders
// "Monday" is stating a rule the engine does not follow.
//
// They take `t` rather than importing it, matching describeDelete() in
// supplierDeleteImpact.js: that keeps them pure and testable, which matters
// because both are branch-per-cadence converters whose wrong answer renders
// perfectly and never throws. The rule itself is usesFirstWeekdayOfMonth in
// constants/planning.js.

import { usesFirstWeekdayOfMonth } from '../constants/planning';

/** Localized weekday name for a 0..6 index (0 = Monday). */
const weekday = (t, dayIndex) => t(`common.weekdays.${dayIndex}`);

/**
 * What a stored preferred order day MEANS under a given cadence — "Monday"
 * on a weekly supplier, "First Monday" on a monthly one.
 *
 * @param {Function} t i18next translator.
 * @param {number} dayIndex 0 = Monday … 6 = Sunday.
 * @param {string} [reviewFrequency] Absent yields the plain weekday name,
 *   which is what the grid's paste parser needs to match an exported value.
 */
export function orderDayLabel(t, dayIndex, reviewFrequency) {
  const day = weekday(t, dayIndex);
  return usesFirstWeekdayOfMonth(reviewFrequency)
    ? t('supplierSetup.orderDayValue.firstOfMonth', { day })
    : day;
}

/**
 * The cadence pair restated as one sentence, for the Add-supplier dialog.
 *
 * Rendered for EVERY cadence including DAILY, whose order-day control is
 * absent rather than disabled — without this, nothing on the dialog says that
 * daily means "any working day". The daily phrase does not interpolate a day;
 * that is asserted in supplierSchedule.test.js rather than defended here with
 * a guard on the value.
 */
export function schedulePreview(t, reviewFrequency, dayIndex) {
  return t(`supplierSetup.schedulePreview.${reviewFrequency.toLowerCase()}`, {
    day: weekday(t, dayIndex),
  });
}
